import { useState, useEffect } from 'react';
import { supabase } from '../supabase';
import type { Database } from '../database.types';
import type { Invoice, InvoiceItem } from '../database.types';

export function useInvoices() {
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchInvoices() {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
          setError('Not authenticated');
          return;
        }

        const { data, error: fetchError } = await supabase
          .from('invoices')
          .select(`
            *,
            customer:customers(
              id,
              first_name,
              last_name,
              email,
              phone
            ),
            work_order:work_orders(
              id,
              work_order_number,
              service_type,
              description
            )
          `)
          .eq('company_id', user.id)
          .order('created_at', { ascending: false });

        if (fetchError) throw fetchError;
        setInvoices(data || []);
      } catch (err) {
        console.error('Error fetching invoices:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch invoices');
      } finally {
        setLoading(false);
      }
    }

    fetchInvoices();
  }, []);

  const addInvoice = async (
    invoiceData: Partial<Invoice>,
    items: Partial<InvoiceItem>[]
  ) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Start a Supabase transaction
      const { data: invoice, error: invoiceError } = await supabase
        .from('invoices')
        .insert([{ ...invoiceData, company_id: user.id }])
        .select()
        .single();

      if (invoiceError) throw invoiceError;

      // Add invoice items
      const { error: itemsError } = await supabase
        .from('invoice_items')
        .insert(
          items.map(item => ({
            ...item,
            invoice_id: invoice.id
          }))
        );

      if (itemsError) throw itemsError;

      setInvoices(prev => [invoice, ...prev]);
      return invoice;
    } catch (err) {
      throw err;
    }
  };

  const updateInvoice = async (id: string, invoiceData: Partial<Invoice>) => {
    try {
      const { data, error } = await supabase
        .from('invoices')
        .update(invoiceData)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      setInvoices(prev => prev.map(invoice => invoice.id === id ? data : invoice));
      return data;
    } catch (err) {
      throw err;
    }
  };

  const deleteInvoice = async (id: string) => {
    try {
      const { error } = await supabase
        .from('invoices')
        .delete()
        .eq('id', id);

      if (error) throw error;
      setInvoices(prev => prev.filter(invoice => invoice.id !== id));
    } catch (err) {
      throw err;
    }
  };

  return { invoices, loading, error, addInvoice, updateInvoice, deleteInvoice };
}